import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import {
  useCreateOrder,
  useSearchCustomers,
  useSearchProductsKomoran,
  useShippingMethods,
  usePaymentGateways,
} from "@/hooks/use-orders";
import { useActiveStore } from "@/hooks/use-active-store";
import { useDebounce } from "@/hooks/use-debounce";
import { fetchJson } from "@/lib/api";
import { newOrderRoute } from "@/lib/router";
import { useReturnOrderPrefill, useCreateOrderFromReturn } from "@/hooks/use-returns";
import { ExchangePricingCard } from "@/components/orders/exchange-pricing-card";
import { computeExchangeTotals } from "@/lib/exchange-totals";
import { isCodOrder } from "@/lib/cod-order";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ORDER_STATUSES } from "@/components/orders/status-badge";
import zipList from "@/lib/ziplist";
import {
  ArrowLeft,
  Search,
  Plus,
  Trash2,
  Loader2,
  User,
  Package,
  ShoppingCart,
  ChevronsUpDown,
  Check,
  Sparkles,
  Wand2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

type Address = {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
};

type LineItem = {
  product_id: number;
  variation_id?: number;
  name: string;
  sku: string;
  price: string;
  quantity: number;
  image?: string;
  attributes?: { name: string; option: string }[];
};

const emptyAddress: Address = {
  first_name: "",
  last_name: "",
  company: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  postcode: "",
  country: "BA",
  email: "",
  phone: "",
};

/** Bosanska množina i particip za „stavka": 1 → stavka nije povezana,
 *  2–4 → stavke nisu povezane, 5+ → stavki nije povezano
 *  (i isto tako za brojeve koji na 11–14 uvijek idu na „stavki"). */
function pluralizeStavka(n: number): { noun: string; verb: string } {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return { noun: "stavka", verb: "nije povezana" };
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return { noun: "stavke", verb: "nisu povezane" };
  return { noun: "stavki", verb: "nije povezano" };
}

export function NewOrderPage() {
  const { storeId } = useActiveStore();
  const navigate = useNavigate();
  const createOrder = useCreateOrder(storeId);
  const { returnId } = newOrderRoute.useSearch();
  const isReturnMode = !!returnId;
  const { data: prefill, isLoading: loadingPrefill, isError: prefillError } = useReturnOrderPrefill(returnId ?? null);
  const createFromReturn = useCreateOrderFromReturn();
  // Zahtjev pripada drugoj trgovini od aktivne — moguće kad je /orders/new?returnId=X
  // pokrenut direktno (bookmark/link) dok je operater prebačen na drugu trgovinu.
  const storeMismatch = isReturnMode && !!prefill && prefill.storeId !== storeId;
  const prefillBlocked = isReturnMode && (prefillError || storeMismatch);

  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerSelected, setCustomerSelected] = useState(false);
  const [billing, setBilling] = useState<Address>({ ...emptyAddress });
  const [shipping, setShipping] = useState<Address>({ ...emptyAddress });
  const [sameAssBilling, setSameAsBilling] = useState(true);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [shippingMethodTitle, setShippingMethodTitle] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodTitle, setPaymentMethodTitle] = useState("");
  const [status, setStatus] = useState("processing");
  const [customerNote, setCustomerNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState<{ code: string; discount_type: string; amount: string; minimum_amount: string; maximum_amount: string }[]>([]);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [shippingCharged, setShippingCharged] = useState(false);
  const [priceDifference, setPriceDifference] = useState("");
  const [prefillApplied, setPrefillApplied] = useState(false);

  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showProductResults, setShowProductResults] = useState(false);
  const [selectedVariableProduct, setSelectedVariableProduct] = useState<any | null>(null);

  const debouncedCustomerSearch = useDebounce(customerSearch);
  const debouncedProductSearch = useDebounce(productSearch);
  const { data: customers, isLoading: loadingCustomers } = useSearchCustomers(storeId, debouncedCustomerSearch);
  const { data: products, isLoading: loadingProducts } = useSearchProductsKomoran(storeId, debouncedProductSearch);
  const { data: shippingMethods, isLoading: loadingShippingMethods } = useShippingMethods(storeId);
  const { data: paymentGateways, isLoading: loadingPaymentGateways } = usePaymentGateways(storeId);

  // Prefill se primjenjuje tačno jednom — poslije toga forma pripada operateru.
  useEffect(() => {
    if (!prefill || prefillApplied) return;
    setBilling((prev) => ({ ...prev, ...prefill.billing }));
    setShipping((prev) => ({ ...prev, ...prefill.shipping }));
    setSameAsBilling(true);
    setLineItems(
      prefill.items.map((it) => ({
        product_id: it.product_id,
        variation_id: it.variation_id,
        name: it.name,
        sku: it.sku,
        price: it.price,
        quantity: 1,
        image: it.image,
      })),
    );
    // Kod krivice firme dostava ide o trošku firme; inače je podrazumijevano naplaćena.
    const companyFault = prefill.reasons.some((r) => r === "pogresan" || r === "sa_greskom");
    setShippingCharged(!companyFault);
    setPrefillApplied(true);
  }, [prefill, prefillApplied]);

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + parseFloat(item.price || "0") * item.quantity, 0);
  }, [lineItems]);

  const couponDiscount = useMemo(() => {
    let discount = 0;
    for (const c of appliedCoupons) {
      const amt = parseFloat(c.amount || "0");
      if (c.discount_type === "percent") {
        discount += subtotal * (amt / 100);
      } else {
        discount += amt;
      }
    }
    return Math.min(discount, subtotal);
  }, [appliedCoupons, subtotal]);

  const total = useMemo(() => {
    return subtotal - couponDiscount + parseFloat(shippingCost || "0");
  }, [subtotal, couponDiscount, shippingCost]);

  const exchange = useMemo(
    () =>
      computeExchangeTotals({
        subtotal,
        couponDiscount,
        priceDifference: parseFloat(priceDifference || "0"),
        shippingCost: parseFloat(shippingCost || "0"),
        // Dostava se u return modu šalje samo uz odabranu metodu (vidi shipping_lines
        // u handleSubmit) — prikaz nikad ne smije obećati dostavu koja neće biti poslana.
        shippingCharged: shippingCharged && !!shippingMethodId,
      }),
    [subtotal, couponDiscount, priceDifference, shippingCost, shippingCharged, shippingMethodId],
  );

  function selectCustomer(customer: any) {
    setCustomerId(customer.id);
    setBilling({
      first_name: customer.billing?.first_name || "",
      last_name: customer.billing?.last_name || "",
      company: customer.billing?.company || "",
      address_1: customer.billing?.address_1 || "",
      address_2: customer.billing?.address_2 || "",
      city: customer.billing?.city || "",
      state: customer.billing?.state || "",
      postcode: customer.billing?.postcode || "",
      country: customer.billing?.country || "BA",
      email: customer.billing?.email || customer.email || "",
      phone: customer.billing?.phone || "",
    });
    setSameAsBilling(true);
    setCustomerSelected(true);
    setCustomerSearch("");
    setShowCustomerResults(false);
  }

  function formatVariationAttributes(attributes?: { option?: string }[]) {
    return attributes?.map((attr) => attr.option).filter(Boolean).join(" / ") || "";
  }

  function addProduct(product: any, variation?: any) {
    const variationName = variation ? formatVariationAttributes(variation.attributes) : "";
    const item: LineItem = {
      product_id: product.id,
      variation_id: variation?.id,
      name: [product.name, variationName].filter(Boolean).join(" - "),
      sku: variation?.sku || product.sku || "",
      price: variation?.price || product.price || "0",
      quantity: 1,
      image: variation?.image || product.image,
      attributes: variation?.attributes,
    };

    const existingIdx = lineItems.findIndex(
      (li) => li.product_id === item.product_id && li.variation_id === item.variation_id
    );
    if (existingIdx >= 0) {
      setLineItems((prev) =>
        prev.map((li, i) => (i === existingIdx ? { ...li, quantity: li.quantity + 1 } : li))
      );
    } else {
      setLineItems((prev) => [...prev, item]);
    }
    setProductSearch("");
    setShowProductResults(false);
    setSelectedVariableProduct(null);
  }

  function removeItem(index: number) {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }

  function updateQuantity(index: number, qty: number) {
    if (qty < 1) return;
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, quantity: qty } : li)));
  }

  function selectShippingMethod(methodId: string) {
    setShippingMethodId(methodId);
    const method = shippingMethods?.find((m: any) => String(m.id) === methodId);
    if (method) {
      setShippingMethodTitle(method.title);
      setShippingCost(method.cost || "0");
    }
  }

  function selectPaymentGateway(gatewayId: string) {
    setPaymentMethod(gatewayId);
    const gateway = paymentGateways?.find((g: any) => g.id === gatewayId);
    if (gateway) {
      setPaymentMethodTitle(gateway.title);
    }
  }

  async function addCoupon() {
    const code = couponCode.trim().toLowerCase();
    if (!code) return;
    if (appliedCoupons.some((c) => c.code === code)) {
      toast.error("Kupon je već dodan");
      return;
    }
    setValidatingCoupon(true);
    try {
      const data: any = await fetchJson(
        `/api/coupons/validate?storeId=${storeId}&code=${encodeURIComponent(code)}`
      );
      const minAmount = parseFloat(data.minimum_amount || "0");
      if (minAmount > 0 && subtotal < minAmount) {
        toast.error(`Minimalni iznos narudžbe za ovaj kupon je ${minAmount.toFixed(2)} KM`);
        return;
      }
      const maxAmount = parseFloat(data.maximum_amount || "0");
      if (maxAmount > 0 && subtotal > maxAmount) {
        toast.error(`Maksimalni iznos narudžbe za ovaj kupon je ${maxAmount.toFixed(2)} KM`);
        return;
      }
      setAppliedCoupons((prev) => [
        ...prev,
        { code: data.code, discount_type: data.discount_type, amount: data.amount, minimum_amount: data.minimum_amount || "", maximum_amount: data.maximum_amount || "" },
      ]);
      setCouponCode("");
      toast.success(`Kupon "${data.code}" primijenjen`);
    } catch (err: any) {
      toast.error(err?.message || "Kupon nije validan");
    } finally {
      setValidatingCoupon(false);
    }
  }

  /** Popunjava samo ona polja koja je AI pouzdano pročitao — ostalo ostaje kako je. */
  function applyParsedAddress(parsed: Record<string, string>) {
    setBilling((prev) => {
      const next = { ...prev };
      for (const key of ["first_name", "last_name", "address_1", "city", "postcode", "phone", "email"] as const) {
        const value = parsed[key];
        if (value) next[key] = value;
      }
      return next;
    });
  }

  function removeCoupon(code: string) {
    setAppliedCoupons((prev) => prev.filter((c) => c.code !== code));
  }

  async function handleSubmit() {
    if (lineItems.length === 0) {
      toast.error("Dodajte barem jednu stavku");
      return;
    }
    if (!billing.first_name || !billing.last_name) {
      toast.error("Unesite ime i prezime kupca");
      return;
    }

    const orderData: any = {
      status,
      billing,
      shipping: sameAssBilling ? billing : shipping,
      line_items: lineItems.map((item) => ({
        product_id: item.product_id,
        variation_id: item.variation_id || undefined,
        quantity: item.quantity,
      })),
      shipping_lines: shippingMethodId
        ? [{ method_id: shippingMethodId, method_title: shippingMethodTitle, total: shippingCost }]
        : [],
      payment_method: paymentMethod,
      payment_method_title: paymentMethodTitle,
      customer_note: customerNote || undefined,
      coupon_lines: appliedCoupons.map((c) => ({ code: c.code })),
      meta_data: internalNote.trim()
        ? [{ key: "_komoran_internal_note", value: internalNote.trim() }]
        : [],
    };

    if (isReturnMode) {
      if (parseFloat(priceDifference || "0") < 0) {
        toast.error("Razlika u cijeni ne može biti negativna");
        return;
      }
      if (shippingCharged && !shippingMethodId) {
        toast.error("Odaberi metodu dostave ili isključi naplatu dostave.");
        return;
      }
      if (exchange.total < 0) {
        toast.error("Cijena dostave ne može biti negativna — provjeri unesenu vrijednost.");
        return;
      }
      if (exchange.total > 0 && !isCodOrder({ payment_method: paymentMethod, payment_method_title: paymentMethodTitle })) {
        toast.error("Narudžba nije pouzeće — kurir ne bi naplatio ništa. Odaberi način plaćanja pouzećem.");
        return;
      }
      orderData.fee_lines = exchange.fee === 0
        ? []
        : [{ name: exchange.feeLabel, total: exchange.fee.toFixed(2), tax_status: "none" }];
      orderData.shipping_lines = shippingMethodId
        ? [{
            method_id: shippingMethodId,
            method_title: shippingMethodTitle,
            total: exchange.shippingTotal.toFixed(2),
          }]
        : [];
    }

    if (customerId) {
      orderData.customer_id = customerId;
    }

    try {
      if (isReturnMode) {
        if (!returnId) return;
        const res = await createFromReturn.mutateAsync({
          id: returnId,
          payload: orderData,
          priceDifference: parseFloat(priceDifference || "0") || 0,
          shippingCharged,
          shippingCost: parseFloat(shippingCost || "0") || 0,
        });
        toast.success(`Narudžba #${res.order.number || res.order.id} kreirana`);
        if (!res.linked) toast.error(res.linkError || "Narudžba nije povezana sa zahtjevom");
        if (res.noteError) toast.error(`Bilješka nije upisana: ${res.noteError}`);
        if (res.totalMismatch) {
          toast.error(
            `Ukupan iznos narudžbe je ${res.totalMismatch.actual.toFixed(2)} KM umjesto očekivanih ${res.totalMismatch.expected.toFixed(2)} KM — provjeri narudžbu prije slanja kuriru.`,
          );
        }
        navigate({ to: "/orders/$orderId", params: { orderId: String(res.order.id) } });
        return;
      }

      const result: any = await createOrder.mutateAsync(orderData);
      toast.success(`Narudžba #${result.number || result.id} kreirana`);
      navigate({ to: "/orders/$orderId", params: { orderId: String(result.id) } });
    } catch (e: any) {
      toast.error(e.message || "Greška pri kreiranju narudžbe");
    }
  }

  return (
    <div className="p-4 flex flex-col gap-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/orders" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-sm font-medium">{isReturnMode ? "Narudžba iz zahtjeva za povrat" : "Nova narudžba"}</h2>
      </div>

      {isReturnMode && prefillError && (
        <div className="ring-1 ring-red-500/30 bg-red-500/5 px-3 py-2 flex items-center gap-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <span className="text-red-500">
            Podaci iz zahtjeva nisu učitani. Osvježi stranicu i pokušaj ponovo.
          </span>
        </div>
      )}

      {isReturnMode && prefill && storeMismatch && (
        <div className="ring-1 ring-red-500/30 bg-red-500/5 px-3 py-2 flex items-center gap-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <span className="text-red-500">
            Zahtjev pripada drugoj trgovini. Otvori zahtjev i pokreni kreiranje narudžbe iz njega.
          </span>
        </div>
      )}

      {isReturnMode && prefill && !storeMismatch && (
        <div className="ring-1 ring-foreground/10 px-3 py-2 flex items-center gap-2 text-xs">
          <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Narudžba iz zahtjeva</span>
          <Link
            to="/returns/$returnId"
            params={{ returnId: prefill.returnId }}
            className="font-mono text-primary hover:underline"
          >
            {prefill.referenceNumber}
          </Link>
        </div>
      )}

      {isReturnMode && prefill && prefill.unlinkedItems.length > 0 && (
        <div className="ring-1 ring-amber-500/30 bg-amber-500/5 px-3 py-2 flex gap-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-0.5">
            <span className="text-amber-500">
              {prefill.unlinkedItems.length} {pluralizeStavka(prefill.unlinkedItems.length).noun} iz zahtjeva{" "}
              {pluralizeStavka(prefill.unlinkedItems.length).verb} s katalogom — dodaj ih ručno.
            </span>
            <span className="text-muted-foreground">
              {prefill.unlinkedItems.map((u) => u.label).join(" · ")}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Customer Search */}
          <section className="ring-1 ring-foreground/10">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b">
              <User className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Kupac</span>
            </div>
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="h-8 text-xs pl-8"
                  placeholder="Pretraži kupce po imenu, emailu ili telefonu..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerResults(true);
                  }}
                  onFocus={() => setShowCustomerResults(true)}
                />
              </div>
              {showCustomerResults && customerSearch.length >= 2 && (
                <div className="mt-2 ring-1 ring-foreground/10 max-h-48 overflow-y-auto">
                  {loadingCustomers && (
                    <p className="p-3 text-xs text-muted-foreground">Učitavanje...</p>
                  )}
                  {!loadingCustomers && (!customers || customers.length === 0) && (
                    <p className="p-3 text-xs text-muted-foreground">Nema rezultata</p>
                  )}
                  {!loadingCustomers &&
                    customers?.map((customer: any) => (
                      <button
                        key={customer.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 border-b last:border-0 transition-colors"
                        onClick={() => selectCustomer(customer)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">
                              {customer.first_name} {customer.last_name}
                            </span>
                            <span className="text-muted-foreground ml-2">{customer.email}</span>
                          </div>
                          {customer.orders_count > 0 && (
                            <span className="text-muted-foreground">
                              {customer.orders_count} narudžbi
                            </span>
                          )}
                        </div>
                        {customer.billing?.phone && (
                          <p className="text-muted-foreground mt-0.5">{customer.billing.phone}</p>
                        )}
                      </button>
                    ))}
                </div>
              )}
              {customerSelected && (
                <div className="mt-2 flex items-center gap-2 bg-muted/30 px-3 py-2 text-xs">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">
                    {billing.first_name} {billing.last_name}
                  </span>
                  <span className="text-muted-foreground">{billing.email}</span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="ml-auto"
                    onClick={() => {
                      setCustomerId(null);
                      setCustomerSelected(false);
                      setBilling({ ...emptyAddress });
                      setShipping({ ...emptyAddress });
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Line Items */}
          <section className="ring-1 ring-foreground/10">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b">
              <ShoppingCart className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Stavke</span>
            </div>
            <div className="p-3 flex flex-col gap-3">
              {/* Product Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="h-8 text-xs pl-8"
                  placeholder="Pretraži proizvode po nazivu ili SKU..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setShowProductResults(true);
                    setSelectedVariableProduct(null);
                  }}
                  onFocus={() => setShowProductResults(true)}
                />
              </div>

              {showProductResults && productSearch.length >= 2 && (
                <div className="ring-1 ring-foreground/10 max-h-64 overflow-y-auto">
                  {loadingProducts && (
                    <p className="p-3 text-xs text-muted-foreground">Učitavanje...</p>
                  )}
                  {!loadingProducts && (!products || products.length === 0) && (
                    <p className="p-3 text-xs text-muted-foreground">Nema rezultata</p>
                  )}
                  {!loadingProducts &&
                    products?.map((product: any) => (
                      <div key={product.id}>
                        <button
                          type="button"
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 border-b transition-colors flex items-center gap-2 ${
                            selectedVariableProduct?.id === product.id ? "bg-muted/50" : ""
                          }`}
                          onClick={() => {
                            if (product.type === "variable" && product.variations?.length > 0) {
                              setSelectedVariableProduct(
                                selectedVariableProduct?.id === product.id ? null : product
                              );
                            } else {
                              addProduct(product);
                            }
                          }}
                        >
                          {product.image ? (
                            <img
                              src={product.image}
                              alt=""
                              className="w-8 h-8 object-cover rounded shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-muted rounded flex items-center justify-center shrink-0">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{product.name}</p>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              {product.sku && <span>SKU: {product.sku}</span>}
                              {product.type === "variable" && product.variations?.length > 0 ? (
                                <span className="text-primary">
                                  {product.variations?.length} varijanti ›
                                </span>
                              ) : product.stock_status === "outofstock" ? (
                                <span className="text-destructive">Nema na stanju</span>
                              ) : product.manage_stock && product.stock_quantity != null ? (
                                <span>Stanje: {product.stock_quantity}</span>
                              ) : (
                                <span className="text-green-600">Na stanju</span>
                              )}
                            </div>
                          </div>
                          <span className="font-medium shrink-0">
                            {product.price ? `${parseFloat(product.price).toFixed(2)} KM` : "—"}
                          </span>
                        </button>
                        {selectedVariableProduct?.id === product.id &&
                          product.variations?.map((variation: any) => (
                            <button
                              key={variation.id}
                              type="button"
                              className="w-full text-left pl-12 pr-3 py-2 text-xs hover:bg-muted/50 border-b transition-colors flex items-center gap-2 bg-muted/20"
                              onClick={() => addProduct(product, variation)}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">
                                  {formatVariationAttributes(variation.attributes) || "Varijanta"}
                                </p>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  {variation.sku && <span>SKU: {variation.sku}</span>}
                                  {variation.stock_status === "outofstock" ? (
                                    <span className="text-destructive">Nema na stanju</span>
                                  ) : variation.stock_quantity != null ? (
                                    <span>Stanje: {variation.stock_quantity}</span>
                                  ) : (
                                    <span className="text-green-600">Na stanju</span>
                                  )}
                                </div>
                              </div>
                              <span className="font-medium shrink-0">
                                {variation.price ? `${parseFloat(variation.price).toFixed(2)} KM` : "—"}
                              </span>
                            </button>
                          ))}
                      </div>
                    ))}
                </div>
              )}

              {/* Added items table */}
              {lineItems.length > 0 && (
                <div className="ring-1 ring-foreground/10">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-2 text-left font-medium">Proizvod</th>
                        <th className="p-2 text-center font-medium w-20">Količina</th>
                        <th className="p-2 text-right font-medium w-24">Cijena</th>
                        <th className="p-2 text-right font-medium w-24">Ukupno</th>
                        <th className="p-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt=""
                                  className="w-7 h-7 object-cover rounded shrink-0"
                                />
                              ) : (
                                <div className="w-7 h-7 bg-muted rounded flex items-center justify-center shrink-0">
                                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium truncate">{item.name}</p>
                                {item.sku && (
                                  <p className="text-muted-foreground">SKU: {item.sku}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min={1}
                              className="h-7 w-16 text-xs text-center mx-auto p-1"
                              value={item.quantity}
                              onChange={(e) => {
                                const next = Number(e.target.value);
                                updateQuantity(idx, Number.isFinite(next) ? Math.max(1, next) : 1);
                              }}
                            />
                          </td>
                          <td className="p-2 text-right">{parseFloat(item.price || "0").toFixed(2)} KM</td>
                          <td className="p-2 text-right font-medium">
                            {(parseFloat(item.price || "0") * item.quantity).toFixed(2)} KM
                          </td>
                          <td className="p-2">
                            <Button variant="ghost" size="icon-xs" onClick={() => removeItem(idx)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {lineItems.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  Pretražite i dodajte proizvode
                </p>
              )}
            </div>
          </section>

          {/* Billing Address */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Adresa za naplatu</span>
            </div>
            <div className="p-3 flex flex-col gap-3">
              <AiAddressPaste onFill={applyParsedAddress} />
              <AddressForm address={billing} onChange={setBilling} showEmail />
            </div>
          </section>

          {/* Shipping Address */}
          <section className="ring-1 ring-foreground/10">
            <div className="flex items-center justify-between px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Adresa za dostavu</span>
              <label className="flex items-center gap-1.5 text-xs">
                <Checkbox
                  checked={sameAssBilling}
                  onCheckedChange={(v) => setSameAsBilling(!!v)}
                />
                Isto kao naplata
              </label>
            </div>
            {!sameAssBilling && (
              <div className="p-3">
                <AddressForm address={shipping} onChange={setShipping} />
              </div>
            )}
          </section>

          {/* Customer Note */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Napomena kupca</span>
            </div>
            <div className="p-3">
              <textarea
                className="w-full text-xs border rounded px-2 py-1.5 min-h-[60px] resize-y bg-background"
                placeholder="Napomena koja će biti vidljiva kupcu..."
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
              />
            </div>
          </section>

          {/* Internal Note */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Interna napomena</span>
            </div>
            <div className="p-3">
              <textarea
                className="w-full text-xs border rounded px-2 py-1.5 min-h-[60px] resize-y bg-background"
                placeholder="Interna napomena (vidljiva samo timu, prikazuje se u pick/pack)..."
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
              />
            </div>
          </section>
        </div>

        {/* Right Sidebar */}
        <div className="flex flex-col gap-4">
          {/* Order Status */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Status</span>
            </div>
            <div className="p-3">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.filter((s) => s.value).map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Payment Method */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Način plaćanja</span>
            </div>
            <div className="p-3">
              <Select value={paymentMethod} onValueChange={selectPaymentGateway} disabled={loadingPaymentGateways}>
                <SelectTrigger className="w-full h-8 text-xs">
                  <SelectValue placeholder={loadingPaymentGateways ? "Učitavanje..." : "Odaberi..."} />
                </SelectTrigger>
                <SelectContent>
                  {paymentGateways?.map((g: any) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.title}
                    </SelectItem>
                  ))}
                  {!loadingPaymentGateways && (!paymentGateways || paymentGateways.length === 0) && (
                    <SelectItem value="_none" disabled>Nema dostupnih metoda</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Shipping Method */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Dostava</span>
            </div>
            <div className="p-3 flex flex-col gap-2">
              <Select value={shippingMethodId} onValueChange={selectShippingMethod} disabled={loadingShippingMethods}>
                <SelectTrigger className="w-full h-8 text-xs">
                  <SelectValue placeholder={loadingShippingMethods ? "Učitavanje..." : "Odaberi..."} />
                </SelectTrigger>
                <SelectContent>
                  {shippingMethods?.map((m: any) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.title}{m.cost ? ` — ${m.cost} KM` : ""}{m.zone ? ` (${m.zone})` : ""}
                    </SelectItem>
                  ))}
                  {!loadingShippingMethods && (!shippingMethods || shippingMethods.length === 0) && (
                    <SelectItem value="_none" disabled>Nema dostupnih metoda</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Cijena dostave</Label>
                <Input
                  className="h-7 text-xs"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Coupons — sakriveno u return modu: kuponi ne mogu tačno odraziti razliku u
              cijeni, a WooCommerce ionako primjenjuje coupon_lines nezavisno od ovog prikaza. */}
          {!isReturnMode && (
            <section className="ring-1 ring-foreground/10">
              <div className="px-3 py-2.5 border-b">
                <span className="text-xs font-medium">Kuponi</span>
              </div>
              <div className="p-3 flex flex-col gap-2">
                <div className="flex gap-1.5">
                  <Input
                    className="h-8 text-xs flex-1"
                    placeholder="Kod kupona..."
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCoupon()}
                    disabled={validatingCoupon}
                  />
                  <Button size="sm" variant="outline" onClick={addCoupon} className="h-8" disabled={validatingCoupon}>
                    {validatingCoupon ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                {appliedCoupons.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {appliedCoupons.map((c) => (
                      <div
                        key={c.code}
                        className="flex items-center justify-between bg-muted/30 px-2 py-1 text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{c.code}</span>
                          <span className="text-green-600">
                            -{c.discount_type === "percent" ? `${c.amount}%` : `${c.amount} KM`}
                          </span>
                        </div>
                        <Button variant="ghost" size="icon-xs" onClick={() => removeCoupon(c.code)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {isReturnMode && (
            <ExchangePricingCard
              shippingCharged={shippingCharged}
              onShippingChargedChange={setShippingCharged}
              priceDifference={priceDifference}
              onPriceDifferenceChange={setPriceDifference}
              subtotal={subtotal}
            />
          )}

          {/* Summary */}
          <section className="ring-1 ring-foreground/10">
            <div className="px-3 py-2.5 border-b">
              <span className="text-xs font-medium">Sažetak</span>
            </div>
            <div className="p-3 flex flex-col gap-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{isReturnMode ? "Artikli" : `Stavke (${lineItems.length})`}</span>
                <span>{subtotal.toFixed(2)} KM</span>
              </div>
              {isReturnMode && exchange.fee !== 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{exchange.feeLabel}</span>
                  <span className={exchange.fee < 0 ? "text-green-600" : undefined}>
                    {exchange.fee.toFixed(2)} KM
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dostava</span>
                <span>
                  {(isReturnMode ? exchange.shippingTotal : parseFloat(shippingCost || "0")).toFixed(2)} KM
                </span>
              </div>
              {!isReturnMode && appliedCoupons.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Popust ({appliedCoupons.length} kupon{appliedCoupons.length > 1 ? "a" : ""})</span>
                  <span className="text-green-600">-{couponDiscount.toFixed(2)} KM</span>
                </div>
              )}
              <div className="border-t pt-1.5 flex justify-between font-medium">
                <span>{isReturnMode ? "Ukupno (otkupnina)" : "Ukupno"}</span>
                <span>{(isReturnMode ? exchange.total : total).toFixed(2)} KM</span>
              </div>
              {isReturnMode && (
                <span className="text-[11px] text-muted-foreground">
                  Kupac plaća kuriru {exchange.total.toFixed(2)} KM.
                </span>
              )}
            </div>
          </section>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={
              createOrder.isPending ||
              createFromReturn.isPending ||
              loadingPrefill ||
              prefillBlocked ||
              lineItems.length === 0
            }
            className="w-full"
          >
            {createOrder.isPending || createFromReturn.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Kreiranje...
              </>
            ) : (
              "Kreiraj narudžbu"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddressForm({
  address,
  onChange,
  showEmail = false,
}: {
  address: Address;
  onChange: (a: Address) => void;
  showEmail?: boolean;
}) {
  const [cityOpen, setCityOpen] = useState(false);

  function update(field: keyof Address, value: string) {
    onChange({ ...address, [field]: value });
  }

  function selectCity(entry: { value: string; label: string }) {
    const cityName = entry.label.replace(/ - \d+$/, "").replace(/ - [A-Z]{2}-\d+$/, "");
    onChange({ ...address, city: cityName, postcode: entry.value });
    setCityOpen(false);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Ime</Label>
          <Input
            className="h-7 text-xs"
            value={address.first_name}
            onChange={(e) => update("first_name", e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Prezime</Label>
          <Input
            className="h-7 text-xs"
            value={address.last_name}
            onChange={(e) => update("last_name", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs">Firma</Label>
        <Input
          className="h-7 text-xs"
          value={address.company}
          onChange={(e) => update("company", e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Adresa 1</Label>
          <Input
            className="h-7 text-xs"
            value={address.address_1}
            onChange={(e) => update("address_1", e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Adresa 2</Label>
          <Input
            className="h-7 text-xs"
            value={address.address_2}
            onChange={(e) => update("address_2", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs">Grad</Label>
        <Popover open={cityOpen} onOpenChange={setCityOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={cityOpen}
              className="h-7 w-full justify-between text-xs font-normal"
            >
              {address.city || "Odaberi grad..."}
              <ChevronsUpDown className="ml-auto h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Pretraži grad..." className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty className="py-3 text-xs">Nema rezultata</CommandEmpty>
                <CommandGroup>
                  {zipList.map((entry) => (
                    <CommandItem
                      key={entry.label}
                      value={entry.label}
                      onSelect={() => selectCity(entry)}
                      className="text-xs"
                    >
                      <Check
                        className={`mr-1.5 h-3 w-3 ${
                          address.city && entry.label.startsWith(address.city) && entry.value === address.postcode
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                      {entry.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Poštanski broj</Label>
          <Input
            className="h-7 text-xs"
            value={address.postcode}
            onChange={(e) => update("postcode", e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Zemlja</Label>
          <Input
            className="h-7 text-xs"
            value={address.country}
            onChange={(e) => update("country", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs">Kanton/Regija</Label>
        <Input
          className="h-7 text-xs"
          value={address.state}
          onChange={(e) => update("state", e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {showEmail && (
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Email</Label>
            <Input
              className="h-7 text-xs"
              type="email"
              value={address.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>
        )}
        <div className={showEmail ? "flex flex-col gap-1" : "flex flex-col gap-1 col-span-2"}>
          <Label className="text-xs">Telefon</Label>
          <Input
            className="h-7 text-xs"
            value={address.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Kupci adresu najčešće pošalju kao jednu poruku ("Marko Marković, Zmaja od Bosne 4,
 * Sarajevo, 061 123 456"). Umjesto ručnog prepisivanja u sedam polja, tekst se zalijepi
 * ovdje i model ga razloži. Grad i poštanski broj se na serveru usklade sa ziplist-om.
 */
function AiAddressPaste({ onFill }: { onFill: (parsed: Record<string, string>) => void }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  async function parse() {
    const value = text.trim();
    if (!value) {
      toast.error("Zalijepite poruku kupca");
      return;
    }
    setLoading(true);
    try {
      const parsed = await fetchJson<Record<string, string>>("/api/address/parse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: value }),
      });
      const filled = Object.entries(parsed).filter(([, v]) => v).length;
      if (filled === 0) {
        toast.error("Nije prepoznato nijedno polje");
        return;
      }
      onFill(parsed);
      setText("");
      toast.success(`Popunjeno ${filled} polja — provjerite prije spremanja`);
    } catch (err: any) {
      toast.error(err?.message || "Parsiranje nije uspjelo");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5 bg-muted/40 p-2 ring-1 ring-foreground/5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Sparkles className="h-3 w-3" />
        Zalijepite poruku kupca — AI popunjava polja
      </div>
      <textarea
        className="w-full text-xs border rounded px-2 py-1.5 min-h-[56px] resize-y bg-background"
        placeholder="Marko Marković, Zmaja od Bosne 4, Sarajevo, 061 123 456"
        value={text}
        disabled={loading}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter šalje, Shift+Enter je novi red — adresa zna biti u više redova.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            parse();
          }
        }}
      />
      <Button
        size="sm"
        variant="secondary"
        className="h-7 text-xs self-start"
        onClick={parse}
        disabled={loading || !text.trim()}
      >
        {loading ? (
          <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Obrađujem...</>
        ) : (
          <><Wand2 className="h-3 w-3 mr-1" />Popuni polja</>
        )}
      </Button>
    </div>
  );
}
